// @flow
import { fromJS, List, Map as ImmutableMap } from 'immutable';
import { getHubDate, getHubMinsSinceMidnight } from '~/core/state/hub/selectors';

import { concat, find, flow, identity, map, reduce, reverse, slice } from 'lodash/fp';
import { createSelector } from 'reselect';
import { listDays } from '~/core/constants/days';
import moment from 'moment-timezone';
import { scheduleType as scheduleTypes, scheduleModesEnum } from '~/core/constants/schedule';

const MINUTES_IN_DAY = 1440;

const getSchedulesSection = (state: { schedules2: typeof fromJS }) => state.schedules2;
// $FlowFixMe
const getComponentId = (_, props) => (props && props.componentId) || '';

export const getDataId = (_: *, props: { componentId: string, saved?: boolean }) => props.componentId.split('__')[1];

const getDayIndex = (_, { dayIndex }) => dayIndex;

const getSavedFlagFromProps = (_, props) => !!props.saved;

const getComponents = createSelector([getSchedulesSection], schedules => schedules.get('components'));

const getSchedulesData = createSelector([getSchedulesSection], schedules => schedules.get('data'));

export const getComponentById = createSelector([getComponents, getComponentId], (components, componentId) =>
    components.get(componentId, ImmutableMap())
);

export const getScheduleFromComponent = createSelector(
    [(state, { componentId }) => getComponentById(state, { componentId })],
    component => component.get('schedule')
);

// remove the guid property (e.g. when the schedule is sent to beekeeper to be saved)
export const getCleanSchedule = createSelector([getScheduleFromComponent], schedule =>
    schedule.map(day => day.map(event => event.remove('guid')))
);

/**
 * Get the schedule being modified by the component if it exists otherwise get the original schedule
 * (the `saved` property allow to force getting the original schedule)
 */
export const getScheduleData = createSelector(
    [getScheduleFromComponent, getSchedulesData, getDataId, getSavedFlagFromProps],
    (schedule, data, dataId, saved) => (saved || !schedule ? data.getIn([dataId, 'schedule']) : schedule)
);

const getLastEvent = createSelector([getScheduleData, getDayIndex], (schedule, dayIndex) =>
    flow(
        concat(listDays),
        slice(dayIndex)(dayIndex + 7),
        reverse,
        map(day => {
            if (!schedule) {
                return fromJS({});
            }

            const daySchedule = schedule.get(day);

            if (!daySchedule) {
                return fromJS({});
            }

            const event = daySchedule.slice(-1).first();
            return event && event.set('dayIndex', listDays.indexOf(day)).set('eventIndex', daySchedule.size - 1);
        }),
        find(event => event !== undefined)
    )(listDays)
);

/**
 * Get the events of a specific day (including the first event starting previously of the requested day)
 * @type {Immutable.List}
 */
export const getDayScheduleEvents = createSelector(
    [getScheduleData, getLastEvent, getDayIndex],
    (schedule, previousEvent, dayIndex) => {
        if (!schedule) {
            return List();
        }

        const mappedDayEvents = schedule
            .get(listDays[dayIndex], List())
            .map((event, i) => event.withMutations(event => event.set('dayIndex', dayIndex).set('eventIndex', i)));

        return List([previousEvent]).concat(mappedDayEvents);
    }
);

const getDateTime = (_, props) => props.dateTime;

export const getScheduleEventByDateTime = createSelector(
    [identity, getDateTime, getComponentId],
    (state, dt, componentId) => {
        const now = moment(dt);
        const currentMinutes = now.diff(now.clone().startOf('day'), 'minutes');

        const currentDayEvents = getDayScheduleEvents(state, {
            componentId,
            dayIndex: now.isoWeekday() - 1
        });

        return (
            // $FlowFixMe
            currentDayEvents.findLast((event, index) => index === 0 || event.get('start') < currentMinutes) ||
            ImmutableMap()
        );
    }
);

export const getCurrentScheduleEvent = createSelector(
    [identity, getHubDate, getHubMinsSinceMidnight, getComponentId, getSavedFlagFromProps],
    (state, now, currentMinutes, componentId, saved) => {
        const currentDayEvents = getDayScheduleEvents(state, {
            componentId,
            dayIndex: now.isoWeekday() - 1,
            saved
        });
        return (
            // $FlowFixMe
            currentDayEvents.findLast((event, index) => index === 0 || event.get('start') <= currentMinutes) ||
            fromJS({})
        );
    }
);

export const getNextScheduleEventInfo = createSelector(
    [identity, getHubDate, getHubMinsSinceMidnight, getComponentId, getScheduleData],
    (state, now, currentMinutes, componentId, schedule) => {
        const currentDayIndex = now.isoWeekday() - 1;
        const flattenSchedule = flow(
            concat(listDays),
            slice(currentDayIndex)(currentDayIndex + 7),
            reduce((flattenList, day) => {
                if (!schedule) {
                    return flattenList;
                }

                const daySchedule = schedule.get(day);

                if (!daySchedule) {
                    return flattenList;
                }

                const dayScheduleMapped = daySchedule.map((event, index) =>
                    event.set('dayIndex', listDays.indexOf(day)).set('eventIndex', index)
                );

                return flattenList.concat(
                    listDays[currentDayIndex] !== day
                        ? dayScheduleMapped
                        : dayScheduleMapped.filter(event => event.get('start') > currentMinutes)
                );
            }, fromJS([]))
        )(listDays);

        if (flattenSchedule.size === 0) {
            return fromJS({});
        }

        const nextEvent = flattenSchedule.get(0);
        const followingEvent = flattenSchedule.get(1);
        const deltaDays = dayIndex => (dayIndex - currentDayIndex + 7) % 7;
        const relativeStart = nextEvent.get('start') + deltaDays(nextEvent.get('dayIndex')) * MINUTES_IN_DAY;
        const relativeEnd = followingEvent.get('start') + deltaDays(followingEvent.get('dayIndex')) * MINUTES_IN_DAY;
        return fromJS({
            value: nextEvent.get('value'),
            relativeStart,
            relativeEnd
        });
    }
);

export const getSelectedDay = createSelector([getComponentById], component => component.get('selectedDay', null));

export const getSelectedEvent = createSelector([getComponentById], component =>
    component.get('selectedEvent', ImmutableMap())
);

export const getScheduleType = createSelector([getSchedulesData, getDataId], (data, dataId) =>
    data.getIn([dataId, 'scheduleType'])
);

export const getScheduleMode = createSelector([getComponentById], component =>
    component.get('scheduleMode', scheduleModesEnum.DEFAULT)
);

export const getCopyDay = createSelector([getComponentById], component => component.get('copyDay', null));

export const getIsSavingSchedule = createSelector([getComponentById], component =>
    component.get('isSavingSchedule', false)
);

export const isShowingPopup = createSelector([getComponentById], component => component.get('showingPopup', false));

export const getIsScheduleValid = createSelector([getSchedulesData, (_, props) => props.id], (data, id) => {
    const schedule = data.getIn([id, 'schedule'], fromJS([]));
    return schedule.size === 0 || (schedule.size === 7 && schedule.every(day => day.size));
});

const scheduleTypePriority = new Map([
    [scheduleTypes.WARM_WHITE_LIGHT, 0],
    [scheduleTypes.TUNEABLE_LIGHT, 1],
    [scheduleTypes.COLOUR_TUNEABLE_LIGHT, 2]
]);

const eventTypeFromEvent = (scheduleType, event) => {
    const schedulePriority = scheduleTypePriority.get(scheduleType);

    if (!schedulePriority || !event) {
        return scheduleType;
    }

    const value = event.get('value');
    const hue = value.get('hue');
    const saturation = value.get('saturation');
    const hsvValue = value.get('value');
    const temperature = value.get('colourTemperature');

    const eventTypeMap = new Map([
        [true, scheduleTypes.WARM_WHITE_LIGHT],
        [!!temperature, scheduleTypes.TUNEABLE_LIGHT],
        [!!(hue && saturation && hsvValue), scheduleTypes.COLOUR_TUNEABLE_LIGHT]
    ]);

    const possibleEventType = eventTypeMap.get(true);
    const possibleEventTypePriority = scheduleTypePriority.get(possibleEventType);

    if (!possibleEventTypePriority) {
        return scheduleType;
    }

    return possibleEventTypePriority >= schedulePriority ? possibleEventType : scheduleType;
};

const getEventFromProps = (_, { event }) => event;
export const getEventTypeFromEvent = createSelector(getScheduleType, getEventFromProps, eventTypeFromEvent);



// WEBPACK FOOTER //
// ./src/core/state/ui/schedule/selectors.js